import getFetcher from 'api/utils/getFetcher';
import { useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import type {
  StructsBillingInvoiceResponse,
  StructsBillingInvoiceResponseRecord,
} from 'types/__generated__/SimInventoryApi';
import { ApiBaseError } from '../utils/shared';

export interface SimInvoicesResultError extends ApiBaseError {}

interface UseInvoicesProps {
  organizationId?: OrgId;
  pageSize?: number;
}

const useInvoices = ({ organizationId, pageSize = 12 }: UseInvoicesProps) => {
  // https://swr.vercel.app/docs/pagination#example-2-cursor-or-offset-based-paginated-api
  const getKey = useCallback(
    (pageIndex: number, previousPageData: StructsBillingInvoiceResponse | null) => {
      if (!organizationId) {
        return null;
      }

      // reached the end
      if (previousPageData && !previousPageData.nextPage) {
        return null;
      }

      const params: Array<[string, string]> = [
        ['orgId', `${organizationId}`],
        ['pageSize', pageSize.toString()],
      ];

      if (pageIndex !== 0 && previousPageData?.nextPage) {
        params.push(['cursor', `${previousPageData.nextPage}`]);
      }

      // add the cursor to the API endpoint
      return `${process.env.VITE_BIZ_API_ROOT}/sim-inventory/billing/invoices?${new URLSearchParams(params)}`;
    },
    [organizationId, pageSize]
  );

  const { data, setSize, size, error, isLoading, mutate, isValidating } = useSWRInfinite<
    StructsBillingInvoiceResponse,
    SimInvoicesResultError
  >(getKey, getFetcher);

  const invoices = useMemo(
    () =>
      data
        ? data.reduce<Array<StructsBillingInvoiceResponseRecord>>((acc, result) => {
            if (result && result.data) {
              return [...acc, ...result.data];
            }
            return acc;
          }, [])
        : [],
    [data]
  );

  const isEmpty = (data?.[0]?.data?.length ?? 0) === 0;
  const end = isEmpty || !!(data && (data[data.length - 1]?.data?.length ?? 0) < pageSize);
  const isRefreshing = isValidating && !!data && data.length === size;
  const isLoadingMore =
    isLoading ||
    (!!(size > 0 && data && (data[size - 1]?.data?.length ?? 0) === 0) && !(isEmpty || end));

  const loadMore = useCallback(() => {
    setSize((lastSize) => lastSize + 1);
  }, [setSize]);

  return {
    invoices,
    end,
    loadMore,
    isEmpty,
    error,
    isLoading,
    isLoadingMore,
    mutate,
    isValidating,
    isRefreshing,
  };
};

export default useInvoices;
